import clsx from 'clsx'
import { MenuItemBlock, type MenuItemBlockProps } from '@/blocks/HeaderBlock/MenuItemBlock'
import { SecondaryNavigationLink } from '@/components/Header/NavigationLink/NavigationLink'
import { NavigationTrigger } from '@/components/Header/NavigationTrigger/NavigationTrigger'
import type { ExpectedBlockType, SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { filterByBlockType, getLinkFieldURL } from '@/services/storyblok/Storyblok.helpers'
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import { storyblokEditable } from '@storyblok/react'
import { Button } from 'ui/src/components/Button/Button'
import { MinusIcon } from 'ui/src/icons/MinusIcon'
import { PlusIcon } from 'ui/src/icons/PlusIcon'
import {
  navigationContent,
  navigationItem,
  navigationItemSubMenu,
  navigationItemSupportMenu,
  navigationMenuWrapper,
  navigationSecondaryItem,
  navigationSecondaryList,
} from '@/components/Header/Header.css'
import { closeIcon, openIcon } from '@/components/Header/NavigationTrigger/NavigationTrigger.css'

export type SubMenuBlockProps = SbBaseBlockProps<{
  name: string
  menuItems: ExpectedBlockType<MenuItemBlockProps>
  currentActiveItem?: string
}> & { variant: 'mobile' | 'desktop' }

export const SubMenuBlock = ({ blok, variant }: SubMenuBlockProps) => {
  const filteredMenuItems = filterByBlockType(blok.menuItems, MenuItemBlock.blockName)

  if (filteredMenuItems.length === 0) {
    return null
  }

  return (
    <NavigationMenuPrimitive.Item
      className={clsx(navigationItem, navigationItemSubMenu, navigationItemSupportMenu)}
      value={blok.name}
      {...storyblokEditable(blok)}
    >
      {variant === 'mobile' ? (
        <NavigationTrigger
          onPointerEnter={(event) => event.preventDefault()}
          onPointerLeave={(event) => event.preventDefault()}
          onPointerMove={(event) => event.preventDefault()}
        >
          <button>
            {blok.name}
            <PlusIcon className={openIcon} size="1rem" />
            <MinusIcon className={closeIcon} size="1rem" />
          </button>
        </NavigationTrigger>
      ) : (
        <NavigationTrigger>
          {/* Cursor is set to auto here because this button serves only as a mouse hover trigger for the dropdown menu */}
          <Button style={{ cursor: 'auto' }} size="medium" variant="secondary">
            {blok.name}
          </Button>
        </NavigationTrigger>
      )}

      <NavigationMenuPrimitive.Content className={clsx(navigationContent, navigationMenuWrapper)}>
        <ul className={navigationSecondaryList}>
          {filteredMenuItems.map((nestedBlock) => (
            <li
              key={nestedBlock._uid}
              className={navigationSecondaryItem}
              value={nestedBlock.name}
              {...storyblokEditable(nestedBlock)}
            >
              <SecondaryNavigationLink href={getLinkFieldURL(nestedBlock.link, nestedBlock.name)}>
                {nestedBlock.name}
              </SecondaryNavigationLink>
            </li>
          ))}
        </ul>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  )
}
SubMenuBlock.blockName = 'subMenu'
